import { useCallback, useEffect, useRef, useState } from 'react';

import { RecommendationsItem } from 'widgets/recommendations/ui/recommendations-item/recommendations-item';
import { getArrayItemByIndex } from 'shared/lib/utils';
import { Spinner } from 'shared/ui/spinner';
import { NoItemsError } from 'shared/ui/errors';
// eslint-disable-next-line boundaries/element-types
import { useUserProgramsData } from 'widgets/programs/model';
import { useIsWalkthroughActive } from 'features/walkthrough';
import { useVideoModalState, VideoModal } from 'features/video-modal';
import {
  LiteracyInsightModal,
  useLiteracyInsightModalStateSelector,
} from 'features/literacy-insight-modal';
import { BOOST_EM_INSIGHT_TYPE, Recommendation } from 'shared/api/insights';

import { RECOMMENDATION_THUMBNAIL_COLORS } from '../../constants';
import { WalkthroughRecommendationsItem } from '../walkthrough-recommendations-item';

import style from './style.module.scss';

type Props = {
  recommendations: Recommendation[];
  filteredDomain?: string;
  isLoading?: boolean;
};

export const RecommendationsContent = ({
  recommendations,
  filteredDomain,
  isLoading,
}: Props) => {
  const recommendationsContainerRef = useRef<HTMLDivElement>(null);

  const [maxRecommendationCardHeight, setMaxRecommendationCardHeight] =
    useState<number | null>(null);

  const { userProgramsData, isLoading: isUserProgramsLoading } =
    useUserProgramsData();

  const isWalkthroughActive = useIsWalkthroughActive();

  const { isVideoModalVisible, videoInfo, hideVideoModal } =
    useVideoModalState();

  const { isLiteracyInsightModalVisible, literacyInsightModalInfo } =
    useLiteracyInsightModalStateSelector();

  let visibleRecommendations = recommendations;

  const onResetMaxRecommendationCardHeight = () => {
    setMaxRecommendationCardHeight(null);
  };

  const calculateMaxRecommendationCardHeight = useCallback(() => {
    if (recommendationsContainerRef.current) {
      const heights = Array.from(
        recommendationsContainerRef.current.children,
      ).map((child) => Math.ceil(child.getBoundingClientRect().height));

      setMaxRecommendationCardHeight(Math.max(...heights));
    }
  }, []);

  // Set max recommendation card height initially and each time after it's reset to null.
  useEffect(() => {
    if (!isLoading && maxRecommendationCardHeight === null) {
      calculateMaxRecommendationCardHeight();
    }
  }, [
    isLoading,
    recommendations,
    calculateMaxRecommendationCardHeight,
    maxRecommendationCardHeight,
  ]);

  // Recalculate the max height for the cases when user moves between the main and recommendations pages.
  useEffect(() => {
    if (maxRecommendationCardHeight) {
      calculateMaxRecommendationCardHeight();
    }
  }, [calculateMaxRecommendationCardHeight, maxRecommendationCardHeight]);

  if (filteredDomain) {
    visibleRecommendations = recommendations.filter(
      ({ content: { productNames } }) => {
        return Object.values(productNames).some(
          (productName) => productName === filteredDomain,
        );
      },
    );
  }

  if (isLoading) {
    return (
      <Spinner
        isCentered={false}
        isDark
        isLarge
        classNames="mx-auto"
        testId="recommendation-spinner"
      />
    );
  }

  if (!recommendations.length && isWalkthroughActive) {
    return <WalkthroughRecommendationsItem />;
  }

  if (!recommendations.length) {
    return <NoItemsError />;
  }

  return (
    <div className={style.contentWrap} ref={recommendationsContainerRef}>
      {visibleRecommendations.map(
        (
          {
            id,
            start_relevant_date,
            app_product_names,
            type,
            content,
            dynamicContent,
            created_date,
          },
          index,
        ) => (
          <RecommendationsItem
            id={id}
            key={id}
            content={content}
            dynamicContent={dynamicContent}
            insightType={type}
            thumbnailFallbackColor={getArrayItemByIndex(
              index,
              RECOMMENDATION_THUMBNAIL_COLORS,
            )}
            programAssetId={app_product_names[0]}
            userPrograms={!isUserProgramsLoading ? userProgramsData : undefined}
            startRelevantDate={start_relevant_date}
            height={
              maxRecommendationCardHeight &&
              app_product_names[0] !== 'Static' &&
              type !== BOOST_EM_INSIGHT_TYPE
                ? maxRecommendationCardHeight
                : 'unset'
            }
            resetMaxRecommendationCardHeight={
              onResetMaxRecommendationCardHeight
            }
            createdDate={created_date}
          />
        ),
      )}
      {isVideoModalVisible && !!videoInfo && (
        <VideoModal onClose={hideVideoModal} {...videoInfo} />
      )}
      {isLiteracyInsightModalVisible && literacyInsightModalInfo && (
        <LiteracyInsightModal
          literacyInsightModalInfo={literacyInsightModalInfo}
        />
      )}
    </div>
  );
};
